//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import moment from 'moment-timezone';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import DatePicker from '@components/DatePicker';
import { Alert } from '@components/atoms/alert/Alert';
import { PrimaryAnchor } from '@components/atoms/anchor/Anchor';
import { InvisibleButton } from '@components/atoms/button/Button';
import Icon from '@components/atoms/icon/Icon';
import Spinner from '@components/atoms/spinner/Spinner';
import {
  BodyBoldText,
  BodyText,
  LinkPrimaryText,
  SubtitleText,
} from '@components/atoms/text/Text';
import Cuid from '@models/Cuid';
import PartialCuid from '@models/PartialCuid';
import { getClientId } from '@redux/selectors/getClientId';
import { getUserTimeZone } from '@redux/selectors/getUserTimeZone';
import { useMediaTrackingContext } from '@tracker/mediaTracking/contexts/MediaTrackingContext';
import { MediaTrackingEventName } from '@tracker/mediaTracking/mediaTrackingModel';
import { useMarkRightsApprovedMutation } from '@uia/details/hooks/useMarkRightsApprovedMutation';
import { UIAFooter } from '@uia/layout/UIAFooter';
import {
  MANAGE_RIGHTS_REQUEST_ARTICLE_URL,
  UIA_RIGHTS_FLOW_TRACKING_IDS,
} from '@uia/rights/constants/uiaRightsConstants';
import {
  getIsBazaarvoiceNetworkSyndicationFrontendEnabled,
  getIsRenderedWithinAnotherWorkflow,
} from '@uia/rights/selectors/getUIARights';
import { withTracking } from '@utils/withTracking';

interface Props {
  expiryTime?: Date;
  handleReturnToRightsOptions: () => void;
  handleSubmit?: () => void;
  onUpdateRights: () => void;
  partialCuid: PartialCuid;
}

export function UIARightsMarkApprovedForm({
  expiryTime,
  handleReturnToRightsOptions,
  handleSubmit,
  onUpdateRights,
  partialCuid,
}: Props) {
  const clientId = useSelector(getClientId);
  const isBazaarvoiceNetworkSyndicationFrontendEnabled = useSelector(
    getIsBazaarvoiceNetworkSyndicationFrontendEnabled,
  );

  // TODO: PD-269777 - Remove once UIA React port is complete
  const isRenderedWithinAnotherWorkflow = useSelector(
    getIsRenderedWithinAnotherWorkflow,
  );
  const timeZone = useSelector(getUserTimeZone);
  // Min date range is exclusive, so subtract 1 day to include todays date
  const minDate = useMemo(
    () => moment().tz(timeZone).subtract(1, 'days').toDate(),
    [timeZone],
  );

  const [expiryDate, setExpiryDate] = useState<Date | undefined>(expiryTime);
  const mutation = useMarkRightsApprovedMutation({ onUpdateRights });
  const { trackEvent } = useMediaTrackingContext();

  return (
    <OverlayWrapper>
      <FormContainer>
        <SubtitleText>Mark as approved</SubtitleText>
        <Form>
          <BodyText>
            You can optionally choose a date when you want rights to expire.
            <Link
              href={MANAGE_RIGHTS_REQUEST_ARTICLE_URL}
              target='__blank'
              trackingId={
                UIA_RIGHTS_FLOW_TRACKING_IDS.MARK_APPROVED_EXPIRY_DATE_LEARN_MORE
              }
            >
              Learn More
            </Link>
          </BodyText>
          <DatePickerLabel>Expiry date (optional)</DatePickerLabel>
          <DatePickerControls>
            <DatePickerStyled
              handleDateChanged={(date) => setExpiryDate(date)}
              minDate={minDate}
              timeZone={timeZone}
              trackingId={
                UIA_RIGHTS_FLOW_TRACKING_IDS.MARK_APPROVED_EXPIRY_DATE_PICKER
              }
              value={expiryDate}
            />
            {expiryDate && (
              <RemoveExpiryDateContainer
                onClick={() => setExpiryDate(undefined)}
                trackingId={
                  UIA_RIGHTS_FLOW_TRACKING_IDS.MARK_APPROVED_REMOVE_EXPIRY_DATE
                }
              >
                <LinkPrimaryText>Remove</LinkPrimaryText>
              </RemoveExpiryDateContainer>
            )}
          </DatePickerControls>
        </Form>
        <InfoContainer>
          <StyledIcon name={'info'} />
          <InfoText>
            This image will be used without requesting permission.
          </InfoText>
        </InfoContainer>
        {isBazaarvoiceNetworkSyndicationFrontendEnabled && (
          <StyledAlert type='warning'>
            Content rights must be requested to be eligible for distribution to
            the Visual Syndication Network.
          </StyledAlert>
        )}
        <ApproveContainer>
          <UIAFooter.ActionButton
            isDisabled={mutation.isLoading}
            onClick={() => {
              trackEvent({
                cuid: Cuid.fromPartial(partialCuid, clientId),
                event: MediaTrackingEventName.RightsManuallyApproved,
              });
              void mutation
                .mutateAsync({
                  cuid: partialCuid,
                  clientId,
                  expirationTime: expiryDate?.getTime(),
                })
                .catch((err) => {
                  console.error(err);
                })
                .finally(() => handleSubmit?.());
            }}
            trackingId={
              UIA_RIGHTS_FLOW_TRACKING_IDS.MARK_APPROVED_SUBMIT_BUTTON
            }
          >
            Approve
            {mutation.isLoading && <StyledSpinner />}
          </UIAFooter.ActionButton>
        </ApproveContainer>
      </FormContainer>
      {!isRenderedWithinAnotherWorkflow && (
        <UIAFooter>
          <UIAFooter.TopSection>
            <StyledText>1 of 1</StyledText>
            <VerticalDivider />
            <UIAFooter.PreviousButton
              onClick={() => handleReturnToRightsOptions()}
              trackingId={
                UIA_RIGHTS_FLOW_TRACKING_IDS.MARK_APPROVED_PREVIOUS_BUTTON
              }
            />
          </UIAFooter.TopSection>
          <UIAFooter.BottomSection>
            <UIAFooter.ActionButton isDisabled={true}>
              Done
            </UIAFooter.ActionButton>
          </UIAFooter.BottomSection>
        </UIAFooter>
      )}
    </OverlayWrapper>
  );
}

const Link = withTracking(
  styled(PrimaryAnchor)(
    ({ theme }) => `
    margin-left: ${theme.spacings[1]};
  `,
  ),
);

const DatePickerStyled = styled(DatePicker)`
  max-width: 176px;
`;

const OverlayWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  width: 100%;
`;

const FormContainer = styled.div(
  ({ theme }) => `
    padding: ${theme.spacings[3]};
  `,
);

const Form = styled.div(
  ({ theme }) => `
    padding: ${theme.spacings[3]};
    margin-top: ${theme.spacings[3]};
    background: ${theme.colors.gray0};
  `,
);

const DatePickerControls = styled.div`
  display: flex;
  justify-content: space-between;
`;

const VerticalDivider = styled.div(
  ({ theme }) => `
    height: ${theme.spacings[6]};
    border-left: ${theme.borderWidths[1]} solid ${theme.colors.gray1};
  `,
);

const StyledAlert = styled(Alert)(
  ({ theme }) => `
  margin: ${theme.spacings[3]} 0;
`,
);

const InfoContainer = styled(BodyText)(
  ({ theme }) => `
  display: flex;
  align-items: center;
  margin-top: ${theme.spacings[3]};
`,
);

const InfoText = styled.span(
  ({ theme }) => `
    margin-left: ${theme.spacings[1]};
  `,
);

const StyledText = styled(BodyText)(
  ({ theme }) => `
  margin: 0 ${theme.spacings[3]};
  color: ${theme.colors.teal1};
`,
);

const StyledSpinner = styled(Spinner)(
  ({ theme }) => `
  width: ${theme.spacings[3]};
  height: ${theme.spacings[3]};
`,
);

const DatePickerLabel = styled(BodyBoldText)(
  ({ theme }) => `
  margin-top: ${theme.spacings[3]};
`,
);

const RemoveExpiryDateContainer = styled(InvisibleButton)``;

const ApproveContainer = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: center;
    margin-top: ${theme.spacings[5]};
  `,
);

const StyledIcon = styled(Icon)(
  ({ theme }) => `
    color: ${theme.colors.aliases.bodySecondary};
  `,
);
